import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import _get from 'lodash/get';
import _size from 'lodash/size';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginImageValidateSize from 'filepond-plugin-image-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import styles from './index.module.scss';

import { If } from 'utils';
import { uploadFile } from 'utils/services';
import { CustomToast, UiKit } from 'components';
import UploadTrigger from './UploadTrigger';
import UploadConstraints from './UploadConstraints';
import { UploadProvider } from './UploadContext';
import { UPLOAD_CATEGORIES } from 'dictionary';

registerPlugin(
  FilePondPluginImageValidateSize,
  FilePondPluginImagePreview,
  FilePondPluginImageExifOrientation,
  FilePondPluginFileValidateType
);

/**
 * @param {Object} props
 * @param {string} props.label - Label for the file upload input
 * @param {string} props.error - Error message to display
 * @param {string} props.value - Current value of the file upload (URL)
 * @param {string} props.tags - Tags to associate with the uploaded file
 * @param {import('utils/types').UploadCategory} props.category - Upload resource category
 * @param {boolean} props.disabled - Whether the file upload is disabled
 * @param {boolean} props.hideFooter - Whether to hide the footer actions
 * @param {string} props.maxFileSize - Maximum file size allowed (in MB)
 * @param {string} props.aspectRatio - Aspect ratio for the file upload preview
 * @param {Array} props.fileTypes - Accepted file types for upload
 * @param {Function} props.onChange - Callback function to handle file change
 * @param {string} props.buttonText - Text for the upload button
 * @param {string} props.className - Additional class names for styling
 * @param {string} props.uploaderClassName - Class name for the file uploader
 * @param {string} props.buttonWrapperClassName - Class name for the button wrapper
 * @param {ReactNode} props.children - Additional children to render inside the component
 */
export default function FileUpload({
  label = '',
  error = '',
  value = '',
  tags = '',
  category = '',
  disabled = false,
  hideFooter = false,
  maxFileSize = '4', // size in MB
  aspectRatio = '0.66',
  fileTypes = ['image/*'],
  onChange = () => {},
  buttonText = 'Upload new image',
  className = '',
  uploaderClassName = '',
  buttonWrapperClassName = '',
  children = null
}) {
  if (!UPLOAD_CATEGORIES.includes(category)) {
    throw new Error(
      `Invalid category: "${category}". Must be one of: ${UPLOAD_CATEGORIES.join(
        ', '
      )}`
    );
  }

  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const prevFilesRef = useRef(files);
  const pondRef = useRef(null);

  const isDisabled = disabled || isUploading;

  useEffect(() => {
    const files = !value ? [] : [{ source: value, options: { type: 'local' } }];
    setFiles(files);
  }, [value]);

  useEffect(() => {
    const prevFiles = prevFilesRef.current;
    if (_size(prevFiles) > 0 && _size(files) === 0) {
      onChange('');
    }
  }, [files, onChange]);

  const load = (url, onLoad, onError) => {
    fetch(url, { cache: 'no-store' })
      .then(res => res.blob())
      .then(onLoad)
      .catch(onError);
  };

  const process = (...dataset) => {
    const [, file, , load, error, progress] = dataset;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('load', () => {
      setIsUploading(true);
      uploadFile(
        {
          file: reader.result,
          tags: ['browser_upload', ...tags.split(',')].join(','),
          category
        },
        ({ lengthComputable, loaded, total }) => {
          progress(lengthComputable, loaded, total);
        },
        data => {
          const url = _get(data, 'secure_url', '');
          onChange(url);
          load('Image uploaded');
        },
        err => {
          const msg = 'Something went wrong while uploading your file';
          error(msg);
          CustomToast({ isNotified: err.notified, msg, type: 'error' });
        },
        () => setIsUploading(false)
      );
    });
  };

  const onUpdateFiles = fileItems => {
    prevFilesRef.current = files;
    const newFiles = fileItems.map(({ file }) => file);
    setFiles(newFiles);
  };

  const initiateImageUpload = () => {
    if (isDisabled) return;
    const browse = _get(pondRef.current, 'browse', () => {});
    browse();
  };

  return (
    <UploadProvider
      value={{
        isDisabled,
        onTriggerUpload: initiateImageUpload
      }}
    >
      <div className={cn(styles.container, className)} data-error={!!error}>
        <UiKit.Label text={label} />
        <FilePond
          ref={pondRef}
          files={files}
          instantUpload
          allowRevert={false}
          disabled={isDisabled}
          server={{ process, load, revert: null }}
          stylePanelLayout="integrated"
          acceptedFileTypes={fileTypes}
          styleLoadIndicatorPosition="right"
          styleProgressIndicatorPosition="right"
          stylePanelAspectRatio={aspectRatio}
          onupdatefiles={onUpdateFiles}
          className={uploaderClassName}
        />
        <UiKit.Error msg={error} hide={disabled} />
        <If test={!hideFooter}>
          <div className={cn(styles.actions, buttonWrapperClassName)}>
            <p className={styles.maxSize}>
              Max <span>{maxFileSize}MB</span>
            </p>
            <UploadTrigger className={styles.button}>
              {buttonText}
            </UploadTrigger>
          </div>
        </If>
        {children}
      </div>
    </UploadProvider>
  );
}

FileUpload.Trigger = UploadTrigger;
FileUpload.Contraints = UploadConstraints;
